import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useNotifications } from '@/contexts/NotificationContext';
import { notificationService } from '@/services/api';
import { UserAvatar } from '@/components/ui/Avatar';
import { cn, formatRelativeDate } from '@/lib/utils';
import { ROUTES } from '@/constants';

const TYPE_LABEL: Record<string, string> = {
  like: 'Liked your post',
  comment: 'Commented on your post',
  reply: 'Replied to your comment',
  repost: 'Reposted your post',
  follow: 'Started following you',
  message: 'Sent you a message',
};

export default function NotificationsPage() {
  const { user } = useAuth();
  const { unreadCount, latest, markRead, markAllRead, refresh } = useNotifications();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const loadNotifications = async () => {
    if (!user) return;
    setLoading(true);
    const { data } = await notificationService.getNotifications(1, 50);
    setNotifications(data || []);
    setLoading(false);
  };

  useEffect(() => {
    void loadNotifications();
    void refresh();
  }, [user?.id]);

  // A notification that arrived while this page was open (or just before it opened).
  useEffect(() => {
    if (!latest) return;
    setNotifications((prev) =>
      prev.some((n) => n.id === latest.id) ? prev : [latest, ...prev],
    );
  }, [latest]);

  const handleMarkAsRead = async (id: string) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    await markRead(id);
  };

  const handleMarkAllAsRead = async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    await markAllRead();
  };

  const handleClick = (n: any) => {
    if (!n.read) void handleMarkAsRead(n.id);
    if (n.type === 'message') {
      navigate(ROUTES.MESSAGES);
    } else if (n.post_id) {
      navigate(ROUTES.POST.replace(':id', n.post_id));
    } else if (n.profiles?.username) {
      navigate(`/@${n.profiles.username}`);
    }
  };

  if (loading) return <div className="flex h-64 items-center justify-center"><div className="skeleton-text h-4 w-32" /></div>;

  return (
    <div className="mx-auto max-w-2xl px-4 pt-4">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-bold">Notifications</h1>
        {unreadCount > 0 && (
          <button onClick={handleMarkAllAsRead} className="text-sm text-brand-600 hover:underline dark:text-brand-400">
            Mark all as read ({unreadCount})
          </button>
        )}
      </div>

      <div className="space-y-2">
        {notifications.length === 0 && (
          <div className="px-4 py-16 text-center">
            <Bell size={48} className="mx-auto mb-4 text-neutral-400 dark:text-neutral-600" />
            <h3 className="font-semibold">No notifications yet</h3>
            <p className="mt-1 text-neutral-500 dark:text-neutral-400">When you get activity, it will appear here.</p>
          </div>
        )}
        {notifications.map((n) => (
          <div
            key={n.id}
            onClick={() => handleClick(n)}
            className={cn(
              'flex items-start gap-3 rounded-xl border border-neutral-200 bg-white p-4 transition-colors hover:bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-900 dark:hover:bg-neutral-800 cursor-pointer',
              !n.read && 'border-brand-100 bg-brand-50 dark:border-brand-900/30 dark:bg-brand-900/10',
            )}
          >
            <UserAvatar src={n.profiles?.avatar_url} size="md" />
            <div className="min-w-0 flex-1">
              <p className="text-sm leading-snug break-words">
                <span className="font-semibold">
                  {n.profiles?.display_name || n.profiles?.username}
                </span>{' '}
                <span className="text-neutral-500 dark:text-neutral-400">{TYPE_LABEL[n.type]}</span>
              </p>
              <div className="mt-1 text-xs text-neutral-400 dark:text-neutral-500">{formatRelativeDate(n.created_at)}</div>
            </div>
            {!n.read && <div className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-brand-600" />}
          </div>
        ))}
      </div>
    </div>
  );
}
